/**
 * Une bascule segmentée JOINTE : n segments under one border (maquettes
 * `Timeline.dc.html`, `BarreDeTheme.dc.html` ; issue #57).
 *
 * The mockup drew two variants and left the choice to the code (« la variante
 * de bascule segmentée (jointe ou séparée) à retenir »): the JOINT one is
 * retained, because it is already what `protocol-timeline.tsx` spends for
 * « frise | arbre » and a second look for one control would make two families
 * of the same gesture in one administration.
 *
 * ## Buttons with `aria-pressed`, not radios
 *
 * The persona's involvement scale is a `radiogroup` of real radios, and its own
 * comment says why: a scale of named crans wants arrow keys and a reported
 * state. THIS control is a pair or a triple of toggles sitting in a dense panel
 * of twenty of them - `role="group"` plus `aria-pressed` is what the frise
 * already uses, it keeps every segment in the tab order, and it does not claim
 * a roving focus the panel never implements. One register, one behaviour.
 *
 * Generic over the value so a caller never casts: the three speeds, the three
 * shapes and the two formats are all closed unions, and a control typed on
 * `string` would let a typo through to `onChange`.
 */

const SEGMENT =
  'inline-flex cursor-pointer items-center gap-1.5 px-3 py-1.5 font-semibold text-sm leading-5 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export interface SegmentedOption<Value extends string> {
  readonly value: Value;
  readonly label: string;
}

export interface SegmentedControlProps<Value extends string> {
  /** The group's accessible name - « Format de l'aperçu », « Rapidité ». */
  readonly label: string;
  readonly options: readonly SegmentedOption<Value>[];
  readonly value: Value;
  readonly disabled?: boolean;
  readonly testId?: string;
  readonly onChange: (next: Value) => void;
}

export function SegmentedControl<Value extends string>({
  label,
  options,
  value,
  disabled = false,
  testId,
  onChange,
}: SegmentedControlProps<Value>) {
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: a <fieldset> groups form controls under a <legend>; these are toggle buttons named by their own labels.
      role="group"
      aria-label={label}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
      className="inline-flex max-w-full overflow-hidden rounded-md border-2 border-bleu-nuit"
    >
      {options.map((option, index) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={`${SEGMENT} ${index === 0 ? '' : 'border-bleu-nuit border-l-2'} ${
            option.value === value ? 'bg-bleu-nuit text-white' : 'bg-white text-bleu-nuit'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
